'use memo';

import { Link } from '@tanstack/react-router';
import TrendBadge from '@/components/Badge/TrendBadge';
import { StatCardHighlight, StatCardPrimaryValue, StatCardSecondaryValue, StatCardValueContainer } from '@/components/Card/StatCard';
import { VStack } from '@/components/Layout';
import Spacer from '@/components/Layout/Spacer';
import { Text } from '@/components/Typography';
import { Separator } from '@/components/ui/separator';
import { defaultNS, useTranslation } from '@/lib/i18n';
import type { AlertsSummaryResponse } from '@/routes/_staff/accesses/_layout/alerts/-query/type';
import { BentoCard, SeeMoreButton } from './BentoCard';

/** カード4: 今月の利用（アクセス数をヒーロー、利用メンバー（在籍比）・アクセス数の推移を縦に並べる）。 */
export function MonthlyUsageCard({
  data,
  totalMembers
}: {
  data: AlertsSummaryResponse | undefined;
  /** 在籍メンバー総数（利用率の母数）。未取得時は分母を出さない */
  totalMembers?: number;
}) {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.accesses.overview.bento'
  });
  const usedMembers = data?.currentMonthUniqueMemberCount ?? 0;
  return <BentoCard title={t('monthlyUsage.title')} headerRight={<SeeMoreButton>
          <Link to="/accesses/list">{t('seeMore')}</Link>
        </SeeMoreButton>}>
      <StatCardHighlight className="py-2">
        <StatCardValueContainer>
          <StatCardPrimaryValue>
            {(data?.currentMonthAccessCount ?? 0).toLocaleString()}
          </StatCardPrimaryValue>
          <StatCardSecondaryValue>
            {t('monthlyUsage.accessCount')}
          </StatCardSecondaryValue>
        </StatCardValueContainer>
      </StatCardHighlight>
      <Spacer variant="xs" />
      <Separator />
      <Spacer variant="xs" />

      {/* ラベル（上）＋値（下）の2段。狭い幅でも改行で崩れない */}
      <VStack className="gap-3">
        <VStack className="gap-0.5">
          <Text variant="labelSmallReg" className="text-neutral-muted-on-fill">
            {t('monthlyUsage.memberCount')}
          </Text>
          <Text as="p" variant="numberSemiLarge">
            {usedMembers.toLocaleString()}
            {totalMembers != null ? <span className="text-pSmall font-semibold text-neutral-muted-on-fill">
                {' / '}
                {totalMembers.toLocaleString()}
              </span> : null}
            <span className="ml-0.5 text-pSmall font-semibold text-neutral-muted-on-fill">
              {t('memberUnit')}
            </span>
          </Text>
        </VStack>

        <VStack className="gap-0.5">
          <Text variant="labelSmallReg" className="text-neutral-muted-on-fill">
            {t('monthlyUsage.monthOverMonth')}
          </Text>
          <TrendBadge value={data?.accessCountChange ?? 0} variant="number" color="neutral" />
        </VStack>
      </VStack>
    </BentoCard>;
}